const LAST_UPDATED = "2026-09-19";
const SUPPORT_EMAIL = "support@shuwa.app";

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-montserrat text-lg font-semibold text-slate-900">{title}</h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 border-b border-border pb-6">
        <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
          Last updated {LAST_UPDATED}
        </span>
        <p className="m-0 text-sm text-slate-500">
          How SHUWA collects, uses, and protects your information.
        </p>
      </div>

      <p>
        SHUWA ("SHUWA", "we", "us") provides a booking and business
        management platform for service businesses (salons, barbers,
        clinics, tutors, and similar) and their customers, available on the
        web, iOS, and Android. This policy explains what information we
        collect, why, and how you can control it.
      </p>

      <Section title="Information we collect">
        <ul className="list-disc space-y-2 pl-5 marker:text-primary">
          <li>
            <strong className="text-slate-800">Account information:</strong>{" "}
            name, email address, phone number, and profile photo if you add
            one. If you sign in with Google or Apple, we receive the name,
            email, and unique account identifier those providers share with
            us.
          </li>
          <li>
            <strong className="text-slate-800">Booking and business data:</strong>{" "}
            appointments, services, staff, and business profile details you
            or a business you interact with create on SHUWA.
          </li>
          <li>
            <strong className="text-slate-800">Payment information:</strong>{" "}
            payments are processed by mobile money providers (MTN Mobile
            Money and Airtel Money). SHUWA does not receive or store your
            mobile money PIN — only the phone number you pay with, a
            payment status and the transaction reference are kept for
            receipts and refunds.
          </li>
          <li>
            <strong className="text-slate-800">Messages:</strong> booking
            confirmations and reminders may be sent via the WhatsApp
            Business Platform, operated by Meta. Your phone number and
            message content for those notifications are processed by Meta
            under its own privacy policy.
          </li>
          <li>
            <strong className="text-slate-800">Push notifications:</strong>{" "}
            if you enable notifications on our mobile app, we store a
            device push token to deliver booking updates.
          </li>
          <li>
            <strong className="text-slate-800">AI scheduling assistant:</strong>{" "}
            if you use the AI-assisted scheduling feature, the text you
            send it is processed by our AI provider to generate a response.
            We do not use this content to train third-party models.
          </li>
          <li>
            <strong className="text-slate-800">Usage data:</strong> basic
            device and log information (app version, device type, crash
            reports) used to keep the service reliable and secure.
          </li>
          <li>
            <strong className="text-slate-800">Search and visit statistics:</strong>{" "}
            we count how often each search is made, how often it finds
            nothing, how many bookings follow it, and how visits that end in
            a booking started (for example from a search engine or a shared
            link, and on which page). These are daily totals only: they are
            not linked to your account, device, IP address or booking, and
            searches that look like a phone number or an email address are
            not counted.
          </li>
        </ul>
      </Section>

      <Section title="How we use this information">
        <ul className="list-disc space-y-2 pl-5 marker:text-primary">
          <li>To create and manage your account and bookings.</li>
          <li>To process payments and refunds through our mobile money providers.</li>
          <li>
            To send booking confirmations, reminders, and support messages
            via WhatsApp, push notification, or email.
          </li>
          <li>To operate the AI scheduling assistant, if you choose to use it.</li>
          <li>To improve search results and see which pages help customers find a business.</li>
          <li>To detect, investigate, and prevent fraud, abuse, and security incidents.</li>
          <li>To comply with legal obligations.</li>
        </ul>
      </Section>

      <Section title="Who we share it with">
        <p>
          We share information only with the service providers needed to
          run SHUWA — payment processing (MTN Mobile Money, Airtel Money), messaging (Meta's
          WhatsApp Business Platform), sign-in (Google, Apple), our AI
          provider for the scheduling assistant, and cloud
          infrastructure/hosting providers — each of whom processes data
          under their own privacy terms and only for the purpose of
          providing their service to us. We do not sell your personal
          information.
        </p>
      </Section>

      <Section title="Data retention">
        <p>
          We keep account and booking data for as long as your account is
          active, and for a limited period afterward as needed for legal,
          accounting, or dispute-resolution purposes, after which it is
          deleted or anonymized.
        </p>
      </Section>

      <Section title="Your choices">
        <ul className="list-disc space-y-2 pl-5 marker:text-primary">
          <li>
            You can review and update your account information from within
            the app.
          </li>
          <li>
            You can disable push notifications in your device settings at
            any time.
          </li>
          <li>
            You can request deletion of your account and associated
            personal data from the{" "}
            <a
              href="/delete-account"
              className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
            >
              account deletion page
            </a>
            . Some information may be retained where required by law (for
            example, payment records for tax purposes).
          </li>
        </ul>
      </Section>

      <Section title="Children's privacy">
        <p>
          SHUWA is not directed to children under 13, and we do not
          knowingly collect personal information from them.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          We may update this policy as SHUWA evolves. We'll update the
          "Last updated" date above when we do, and post material changes
          on this page.
        </p>
      </Section>

      <div className="rounded-xl bg-muted p-5">
        <h2 className="font-montserrat text-lg font-semibold text-slate-900">Contact us</h2>
        <p className="mt-2 mb-0">
          Questions about this policy or your data? Email us at{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </div>
    </>
  );
}
